/**
 * Init and resume animations for the SpaceCode CLI.
 *
 * Displays a branded logo box and animated cascade of setup steps
 * when launching or resuming a session.
 */

import chalk from 'chalk';

export interface InitAnimationOptions {
  version: string;
  username: string;
  sessionName: string;
  ttyHost: string;
  fileCount: number;
  repo?: string;
  branch?: string;
  fast?: boolean;
}

export interface ResumeAnimationOptions {
  sessionName: string;
  turnCount: number;
  fast?: boolean;
}

const STAGGER_MS = 150;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function write(text: string): void {
  process.stderr.write(text);
}

/**
 * Display a logo box and animated cascade of setup steps.
 */
export async function showInitAnimation(opts: InitAnimationOptions): Promise<void> {
  const stagger = opts.fast ? 0 : STAGGER_MS;

  // Logo box
  const label = `  \u25C6 SpaceCode v${opts.version}`;
  const innerWidth = Math.max(label.length + 2, 32);
  const padded = label.padEnd(innerWidth);
  const rule = '\u2500'.repeat(innerWidth);

  write(`${chalk.cyan('\u256D' + rule + '\u256E')}\n`);
  write(`${chalk.cyan('\u2502')}${chalk.bold(padded)}${chalk.cyan('\u2502')}\n`);
  write(`${chalk.cyan('\u2570' + rule + '\u256F')}\n`);

  // Cascade steps
  const steps = [
    `Authenticated as @${opts.username}`,
    `Session ${opts.sessionName} created`,
    `Connected to ${opts.ttyHost}`,
    `File sync active (${opts.fileCount} files)`,
  ];

  for (const step of steps) {
    if (stagger > 0) await delay(stagger);
    write(`${chalk.green('\u2713')} ${step}\n`);
  }

  // Context line
  if (opts.repo || opts.branch) {
    if (stagger > 0) await delay(stagger);
    const parts: string[] = [];
    if (opts.repo) parts.push(`repo: ${opts.repo}`);
    if (opts.branch) parts.push(`branch: ${opts.branch}`);
    write(`${chalk.dim(parts.join('  '))}\n`);
  }

  write('\n');
}

/**
 * Display a shorter resume banner.
 */
export async function showResumeAnimation(opts: ResumeAnimationOptions): Promise<void> {
  const stagger = opts.fast ? 0 : STAGGER_MS;
  if (stagger > 0) await delay(stagger);
  write(`${chalk.green('\u2713')} Resumed "${opts.sessionName}" (${opts.turnCount} turns)\n`);
  write('\n');
}
